import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db, app } from "./base.js";
import {
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { SyncLoader } from "react-spinners";

export const AuthContext = React.createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authUserData, setAuthUserData] = useState(null);
  const [pending, setPending] = useState(true);

  // Resolve (and claim) the access grant for this signed-in user via the
  // claimAccess callable. Access is keyed by lowercased email; rules can't
  // lowercase the token email to match the doc id, so the first read MUST go
  // through the server (gotcha #1). Returns the resolved tier (a valid grant)
  // or null (no access / expired). Admins skip this — they aren't seat-gated.
  async function getUserAccount(user) {
    if (user == null) {
      setAuthUserData(null);
      return;
    }

    const userRef = doc(db, "users", user.uid);
    const userDocSnap = await getDoc(userRef);

    let userData;
    if (!userDocSnap.exists()) {
      // The single auth chokepoint that lazily creates the unified "user" doc
      // for both Google and email sign-in. Access is pure server-side data in
      // the `access` collection, not a field on this doc.
      userData = {
        userID: user.uid,
        email: user.email,
        userType: "user",
        // First-login flag for the Terms & Conditions gate (see Login.jsx).
        // Brand-new users start unaccepted and must accept once.
        terms_accepted: false,
      };
      await setDoc(userRef, userData, { merge: true });
    } else {
      userData = userDocSnap.data();
    }

    let tier = null;
    if (userData.userType !== "admin") {
      try {
        const fn = httpsCallable(
          getFunctions(app, "us-central1"),
          "claimAccess",
        );
        const { data } = await fn();
        tier = data.hasAccess ? data.tier : null;
      } catch (error) {
        // Treat any callable failure as "no access" rather than crashing the
        // auth provider (gotcha #10).
        console.error("claimAccess failed", error);
      }
    }

    userData.tier = tier;
    // `license` no longer drives access. Gates read authUserData.tier (via
    // effectiveTier) and this derived hasAccess flag (admin OR a valid grant).
    userData.hasAccess = userData.userType === "admin" || tier !== null;
    setAuthUserData(userData);
  }






  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setPending(true)
      setCurrentUser(user)
      await getUserAccount(user)
      setPending(false)
    });
    return unsubscribe;
  }, []);

  if(pending) {
    return (
      <div className="loadingScreen">
        <SyncLoader color={"#e7b33e"} loading={pending} size={20} />
      </div>
    )
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        authUserData,
        setAuthUserData
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};